import React, { useEffect, useState } from 'react'
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Fab,
  Alert,
  Snackbar,
  Menu,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'
import RepeatIcon from '@mui/icons-material/Repeat'
import {
  makeGetAllRequest,
  makePostRequest,
  makePatchRequest,
  makeDeleteRequest,
} from '../../utils/api'
import urls from '../../utils/urls'

// Styled components for dark theme matching list/detail pattern
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.05)',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
    transform: 'translateY(-4px)',
  },
}))

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 20,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 28px',
  boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
}))

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  background: 'linear-gradient(145deg, #1e1e2e 0%, #252540 100%)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  transition: 'all 0.3s ease',
  border: '1px solid rgba(255,255,255,0.05)',
  '&:hover': {
    boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.1)',
  },
}))

const ItemLibrary = ({ onBack, showSnackbar }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    default_quantity: 1,
    default_unit: 'pcs',
    default_aisle: '',
    frequency: 'never',
    custom_x_days: null,
    default_on_list: false,
    brand: '',
    package_size: '',
    notes: '',
  })

  const units = ['pcs', 'kg', 'g', 'L', 'ml', 'lb', 'oz', 'cup', 'tbsp', 'tsp', 'package', 'bottle', 'can', 'box', 'bag']
  const frequencies = [
    { value: 'never', label: 'Jamais' },
    { value: 'weekly', label: 'Hebdomadaire (7 jours)' },
    { value: 'biweekly', label: 'Bihebdomadaire (14 jours)' },
    { value: 'monthly', label: 'Mensuel (30 jours)' },
    { value: 'quarterly', label: 'Trimestriel (90 jours)' },
    { value: 'annual', label: 'Annuel (365 jours)' },
    { value: 'custom_x_days', label: 'Jours Personnalisés' },
  ]

  useEffect(() => {
    fetchItems()
  }, [])

  // `silent` : le contenu est monté sous `{!loading && ...}`. Repasser par
  // loading le démonte, la page perd sa hauteur et le scroll saute en haut.
  // Les recharges qui suivent une action utilisateur sont donc silencieuses ;
  // seul le tout premier chargement affiche le spinner.
  const fetchItems = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true)
    try {
      const response = await makeGetAllRequest(urls.itemLibrary, {}, true)
      if (response?.status === 200) {
        setItems(response.data.results || [])
      }
    } catch (error) {
      console.error('Error fetching library items:', error)
      showSnackbar('Erreur lors du chargement des articles de la bibliothèque', 'error')
    } finally {
      if (!silent) setLoading(false)
    }
  }

  const handleAddItem = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Le nom de l\'article est requis', 'error')
      return
    }

    try {
      const response = await makePostRequest(urls.itemLibrary, formData, {}, true)
      if (response?.status === 201) {
        await fetchItems({ silent: true })
        setShowAddDialog(false)
        resetForm()
        showSnackbar('Article ajouté à la bibliothèque !', 'success')
      }
    } catch (error) {
      console.error('Error adding item:', error)
      showSnackbar('Erreur lors de l\'ajout de l\'article à la bibliothèque', 'error')
    }
  }

  const handleUpdateItem = async () => {
    if (!formData.name.trim()) {
      showSnackbar('Le nom de l\'article est requis', 'error')
      return
    }

    try {
      const response = await makePatchRequest(
        `${urls.itemLibrary}${editingItem.id}/`,
        formData,
        {},
        true
      )
      if (response?.status === 200) {
        await fetchItems({ silent: true })
        setEditingItem(null)
        resetForm()
        showSnackbar('Article mis à jour !', 'success')
      }
    } catch (error) {
      console.error('Error updating item:', error)
      showSnackbar('Erreur lors de la mise à jour de l\'article', 'error')
    }
  }

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet article de votre bibliothèque ?')) {
      try {
        const response = await makeDeleteRequest(`${urls.itemLibrary}${itemId}/`, {}, true)
        if (response?.status === 204) {
          await fetchItems({ silent: true })
          showSnackbar('Article supprimé de la bibliothèque !', 'success')
        }
      } catch (error) {
        console.error('Error deleting item:', error)
        showSnackbar('Erreur lors de la suppression de l\'article', 'error')
      }
    }
  }

  const handleToggleDefaultOnList = async (item) => {
    try {
      const response = await makePatchRequest(
        `${urls.itemLibrary}${item.id}/`,
        { default_on_list: !item.default_on_list },
        {},
        true
      )
      if (response?.status === 200) {
        await fetchItems({ silent: true })
        showSnackbar(
          item.default_on_list 
            ? 'Article retiré de la liste d\'ajout automatique' 
            : 'L\'article sera ajouté automatiquement aux nouvelles listes !',
          'success'
        )
      }
    } catch (error) {
      console.error('Error updating item:', error)
      showSnackbar('Erreur lors de la mise à jour de l\'article', 'error')
    }
  }

  const handleEdit = (item) => {
    setEditingItem(item)
    setFormData({
      name: item.name,
      default_quantity: item.default_quantity,
      default_unit: item.default_unit,
      default_aisle: item.default_aisle || '',
      frequency: item.frequency,
      custom_x_days: item.custom_x_days,
      default_on_list: item.default_on_list,
      brand: item.brand || '',
      package_size: item.package_size || '',
      notes: item.notes || '',
    })
    setShowAddDialog(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      default_quantity: 1,
      default_unit: 'pcs',
      default_aisle: '',
      frequency: 'never',
      custom_x_days: null,
      default_on_list: false,
      brand: '',
      package_size: '',
      notes: '',
    })
  }

  const handleCloseDialog = () => {
    setShowAddDialog(false)
    setEditingItem(null)
    resetForm()
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0f0f1e', pb: 4 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={onBack}
            sx={{ 
              mb: 2,
              borderRadius: 20,
              textTransform: 'none',
              fontWeight: 600,
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            Retour aux listes
          </Button>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" gutterBottom sx={{ color: 'white', fontWeight: 700 }}>
                Ma Bibliothèque
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                Gérez vos articles favoris et définissez les achats récurrents
              </Typography>
            </Box>
            
            <ActionButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddDialog(true)}
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7e8ef5 0%, #8b5db8 100%)',
                },
              }}
            >
              Ajouter un Article
            </ActionButton>
          </Box>
        </Box>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <StyledPaper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#667eea', fontWeight: 700 }}>
                {items.length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                Articles Total
              </Typography>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledPaper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#4caf50', fontWeight: 700 }}>
                {items.filter(i => i.default_on_list).length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                Ajout Automatique
              </Typography>
            </StyledPaper>
          </Grid>
          <Grid item xs={12} sm={4}>
            <StyledPaper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h3" sx={{ color: '#ff9800', fontWeight: 700 }}>
                {items.filter(i => i.frequency !== 'never').length}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 1 }}>
                Articles Récurrents
              </Typography>
            </StyledPaper>
          </Grid>
        </Grid>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress sx={{ color: '#667eea' }} />
          </Box>
        )}

        {/* Items Grid */}
        {!loading && items.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <LocalOfferIcon sx={{ fontSize: 80, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
              Aucun article dans votre bibliothèque
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)' }}>
              Ajoutez vos articles favoris pour les réutiliser facilement
            </Typography>
          </Box>
        )}

        {!loading && items.length > 0 && (
          <Grid container spacing={3}>
            {items.map((item) => (
              <Grid item xs={12} sm={6} md={4} key={item.id}>
                <StyledCard>
                  <CardContent>
                    {/* Header with Star */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ flex: 1, mr: 1 }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {item.default_quantity} {item.default_unit}
                        </Typography>
                      </Box>
                      
                      <IconButton
                        size="small"
                        onClick={() => handleToggleDefaultOnList(item)}
                        sx={{ 
                          color: item.default_on_list ? '#ffc107' : 'rgba(255,255,255,0.3)',
                        }}
                      >
                        {item.default_on_list ? <StarIcon /> : <StarBorderIcon />}
                      </IconButton>
                    </Box>

                    {/* Details */}
                    <Box sx={{ mb: 2 }}>
                      {item.default_aisle && (
                        <Chip 
                          label={item.default_aisle}
                          size="small"
                          sx={{
                            mr: 1,
                            mb: 1,
                            bgcolor: 'rgba(102, 126, 234, 0.2)',
                            color: '#667eea',
                            border: '1px solid rgba(102, 126, 234, 0.3)',
                          }}
                        />
                      )}
                      
                      {item.frequency !== 'never' && (
                        <Chip 
                          icon={<RepeatIcon sx={{ fontSize: 16 }} />}
                          label={frequencies.find(f => f.value === item.frequency)?.label || item.frequency}
                          size="small"
                          sx={{
                            mr: 1,
                            mb: 1,
                            bgcolor: 'rgba(255, 152, 0, 0.2)',
                            color: '#ff9800',
                            border: '1px solid rgba(255, 152, 0, 0.3)',
                          }}
                        />
                      )}

                      {item.is_due && (
                        <Chip 
                          label="À acheter" 
                          size="small" 
                          sx={{ 
                            mr: 1,
                            mb: 1,
                            bgcolor: 'rgba(76, 175, 80, 0.2)',
                            color: '#4caf50',
                            border: '1px solid rgba(76, 175, 80, 0.3)',
                          }}
                        />
                      )}

                      {item.default_on_list && (
                        <Chip 
                          label="Auto-ajout" 
                          size="small" 
                          sx={{ 
                            mb: 1,
                            bgcolor: 'rgba(255, 193, 7, 0.2)',
                            color: '#ffc107',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                          }}
                        />
                      )}
                    </Box>

                    {item.brand && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                        Marque: {item.brand}
                      </Typography>
                    )}

                    {item.package_size && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
                        Taille: {item.package_size}
                      </Typography>
                    )}

                    {item.notes && (
                      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1, fontStyle: 'italic' }}>
                        {item.notes}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(item)}
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          color: '#667eea',
                          bgcolor: 'rgba(102, 126, 234, 0.1)',
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteItem(item.id)}
                      sx={{ 
                        color: 'rgba(255,255,255,0.7)',
                        '&:hover': {
                          color: '#f44336',
                          bgcolor: 'rgba(244, 67, 54, 0.1)',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </StyledCard>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Add/Edit Dialog */}
        <Dialog
          open={showAddDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editingItem ? 'Modifier l\'Article de la Bibliothèque' : 'Ajouter un Article à la Bibliothèque'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Nom de l'Article *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Quantité"
                  type="number"
                  value={formData.default_quantity}
                  onChange={(e) => setFormData({ ...formData, default_quantity: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Unité</InputLabel>
                  <Select
                    value={formData.default_unit}
                    label="Unité"
                    onChange={(e) => setFormData({ ...formData, default_unit: e.target.value })}
                  >
                    {units.map(unit => (
                      <MenuItem key={unit} value={unit}>{unit}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Rayon"
                  value={formData.default_aisle}
                  onChange={(e) => setFormData({ ...formData, default_aisle: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={formData.frequency === 'custom_x_days' ? 8 : 12}>
                <FormControl fullWidth>
                  <InputLabel>Fréquence</InputLabel>
                  <Select
                    value={formData.frequency}
                    label="Fréquence"
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  >
                    {frequencies.map(freq => (
                      <MenuItem key={freq.value} value={freq.value}>{freq.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              
              {formData.frequency === 'custom_x_days' && (
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Jours"
                    type="number"
                    value={formData.custom_x_days || ''}
                    onChange={(e) => setFormData({ ...formData, custom_x_days: parseInt(e.target.value) })}
                  />
                </Grid>
              )}
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Marque"
                  value={formData.brand}
                  onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Taille du Paquet"
                  value={formData.package_size}
                  onChange={(e) => setFormData({ ...formData, package_size: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={2}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.default_on_list}
                      onChange={(e) => setFormData({ ...formData, default_on_list: e.target.checked })}
                    />
                  }
                  label="Ajouter automatiquement aux nouvelles listes de courses"
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                  Lorsque activé, cet article sera automatiquement ajouté à chaque nouvelle liste de courses que vous créez
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Annuler</Button>
            <Button 
              onClick={editingItem ? handleUpdateItem : handleAddItem}
              variant="contained"
            >
              {editingItem ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default ItemLibrary
